import { API_BASE_URL } from "../config/api";

export type AccountantStatusSummary = {
  vendorBills: {
    processed: number;
    pending: number;
    total: number;
  };
  customerInvoices: {
    processed: number;
    pending: number;
    total: number;
  };
  bankTransactions: {
    categorized: number;
    needsReview: number;
    total: number;
  };
  otherEntries: {
    posted: number;
    pending: number;
    total: number;
  };
};

export type MonthDetailBills = {
  month: string;
  year: number;
  totalUploaded: number;
  processed: number;
  pending: number;
  vendors: Array<{
    vendorName: string;
    billCount: number;
    totalAmount: number;
    status: string;
    statusType: 'success' | 'warning' | 'error';
  }>;
  pagination?: {
    total: number;
    limit: number;
    offset: number;
    has_more: boolean;
  };
};

export type MonthDetailInvoices = {
  month: string;
  year: number;
  totalCreated: number;
  processed: number;
  pending: number;
  customers: Array<{
    customerName: string;
    invoiceCount: number;
    totalAmount: number;
    status: string;
    statusType: 'success' | 'warning' | 'error';
  }>;
  pagination?: {
    total: number;
    limit: number;
    offset: number;
    has_more: boolean;
  };
};

export type MonthDetailTransactions = {
  month: string;
  year: number;
  fetched: number;
  categorized: number;
  needsReview: number;
  accounts: Array<{
    accountName: string;
    transactionsFetched: number;
    categorized: number;
    needsReview: number;
  }>;
};

export type PendingItems = {
  pendingBills: {
    count: number;
    description: string;
  };
  pendingInvoices: {
    count: number;
    description: string;
  };
  transactionsNeedingReview: {
    count: number;
    description: string;
  };
};

export type MonthWorkHistory = {
  month: string;
  year: number;
  billsProcessed: number;
  billsPending: number;
  invoicesProcessed: number;
  invoicesPending: number;
  transactionsCategorized: number;
  transactionsReview: number;
  booksStatus: 'pending' | 'in_progress' | 'closed';
};

export type PerformanceInsights = {
  completionPercentage: number;
  volumeChangePercentage: number;
  volumeChangeDirection: 'higher' | 'lower';
  mainPendingReason: string;
  consecutiveClosedMonths: number;
};




function qs(params: Record<string, any>): string {
  const entries = Object.entries(params).filter(([, v]) => v !== undefined && v !== null && v !== '');
  if (!entries.length) return '';
  const search = new URLSearchParams(entries as any).toString();
  return `?${search}`;
}

export async function getSummary(params: { year?: number; month?: number; companyId?: number } = {}): Promise<AccountantStatusSummary> {
  const url = `${API_BASE_URL}/api/accountant-status/summary${qs(params)}`;
  console.log('Fetching accountant status from:', url);
  const res = await fetch(url);
  if (!res.ok) {
    const errorText = await res.text();
    console.error('Accountant status fetch failed:', res.status, errorText);
    throw new Error(`Failed to fetch accountant status summary: ${res.status} - ${errorText}`);
  }
  return (await res.json()) as AccountantStatusSummary;
}

export async function getMonthDetailBills(params: { year: number; month: number; companyId?: number; limit?: number; offset?: number }): Promise<MonthDetailBills> {
  const url = `${API_BASE_URL}/api/accountant-status/month-detail-bills${qs(params)}`;
  const res = await fetch(url);
  if (!res.ok) throw new Error(`Failed to fetch month detail bills`);
  return (await res.json()) as MonthDetailBills;
}

export async function getMonthDetailInvoices(params: { year: number; month: number; companyId?: number; limit?: number; offset?: number }): Promise<MonthDetailInvoices> {
  const url = `${API_BASE_URL}/api/accountant-status/month-detail-invoices${qs(params)}`;
  const res = await fetch(url);
  if (!res.ok) throw new Error(`Failed to fetch month detail invoices`);
  return (await res.json()) as MonthDetailInvoices;
}

export async function getMonthDetailTransactions(params: { year: number; month: number; companyId?: number }): Promise<MonthDetailTransactions> {
  const url = `${API_BASE_URL}/api/accountant-status/month-detail-transactions${qs(params)}`;
  const res = await fetch(url);
  if (!res.ok) throw new Error(`Failed to fetch month detail transactions`);
  return (await res.json()) as MonthDetailTransactions;
}

export async function getPendingItems(params: { year?: number; month?: number; companyId?: number } = {}): Promise<PendingItems> {
  const url = `${API_BASE_URL}/api/accountant-status/pending-items${qs(params)}`;
  const res = await fetch(url);
  if (!res.ok) throw new Error(`Failed to fetch pending items`);
  return (await res.json()) as PendingItems;
}

export async function getWorkHistory(params: { companyId?: number } = {}): Promise<MonthWorkHistory[]> {
  const url = `${API_BASE_URL}/api/accountant-status/work-history${qs(params)}`;
  const res = await fetch(url);
  if (!res.ok) throw new Error(`Failed to fetch work history`);
  return (await res.json()) as MonthWorkHistory[];
}

export async function getPerformanceInsights(params: { year?: number; month?: number; companyId?: number } = {}): Promise<PerformanceInsights> {
  const url = `${API_BASE_URL}/api/accountant-status/performance-insights${qs(params)}`;
  const res = await fetch(url);
  if (!res.ok) throw new Error(`Failed to fetch performance insights`);
  return (await res.json()) as PerformanceInsights;
}

